/**
 * FlowWidget — floating AI assistant for the CRM dashboard.
 *
 * States: idle → suggestion → drafting
 * Visual language aligned with shadcn/ui: semantic tokens, subtle depth,
 * 1px borders, md radius, visible focus rings (Radix-style accessibility).
 * All mutations are approval-gated through FlowContext (never auto-execute).
 */

import { useEffect, useLayoutEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { SiriOrb } from '@/components/ui/siri-orb';
import { AiAssistat } from '@/components/ui/ai-assistat';
import { useFlow } from '@/context/FlowContext';
import { supabase } from '@/lib/supabase';
import { computePanelAnchorStyle } from '@/lib/flowPanelAnchor';
import { highlightNode } from '@/lib/flowDomUtils';
import {
  X,
  ArrowRight,
  Calendar,
  TrendingUp,
  Mail,
  Check,
  ChevronRight,
  Copy,
  CheckCircle2,
  MessageSquare,
  Activity,
  Loader2,
} from 'lucide-react';
import type { FlowSuggestion } from '@/lib/flowValidation';

// ─── Type icon map ────────────────────────────────────────────────────────────
const SuggestionIcon = ({ type }: { type: FlowSuggestion['type'] }) => {
  if (type === 'move_stage') return <ArrowRight className="h-3.5 w-3.5" />;
  if (type === 'high_priority') return <TrendingUp className="h-3.5 w-3.5" />;
  return <Calendar className="h-3.5 w-3.5" />;
};

const urgencyColor: Record<FlowSuggestion['urgency'], string> = {
  high: '#f87171',
  medium: '#fb923c',
  low: '#4ade80',
};

/** Matches shadcn Button focus treatment for non-Button controls */
const focusRing =
  'focus-visible:outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]';

// ─── Sub-components ───────────────────────────────────────────────────────────

function IdleOrb({
  count,
  onClick,
}: {
  count: number;
  onClick: () => void;
}) {
  return (
    <motion.button
      type="button"
      onClick={onClick}
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 340, damping: 24 }}
      aria-label="Open Flow by RaiseBeacon"
      className={cn(
        'relative flex h-[5.5rem] w-[5.5rem] flex-shrink-0 items-center justify-center rounded-full border border-border/70 bg-card/90 p-1.5 shadow-lg ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:border-primary/35 dark:bg-card/80 dark:ring-white/10',
        focusRing
      )}
    >
      <SiriOrb
        size="72px"
        animationDuration={16}
        className="pointer-events-none drop-shadow-md"
      />
      <AnimatePresence>
        {count > 0 && (
          <motion.span
            key="badge"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
            className="absolute -right-0.5 -top-0.5 z-10 flex h-5 min-w-5 items-center justify-center rounded-full border border-background bg-destructive px-1 text-[10px] font-medium text-destructive-foreground shadow-sm"
            aria-label={`${count} pending suggestions`}
          >
            {count}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

function SuggestionPanel() {
  const {
    currentSuggestion,
    pendingSuggestions,
    approveSuggestion,
    dismissSuggestion,
    requestDraft,
    closeWidget,
    getContact,
  } = useFlow();

  const [isApproving, setIsApproving] = useState(false);

  useEffect(() => {
    if (currentSuggestion) {
      highlightNode(currentSuggestion.contactId);
    }
  }, [currentSuggestion?.contactId]);

  if (!currentSuggestion) return null;

  const contact = getContact(currentSuggestion.contactId);

  const handleApprove = async () => {
    setIsApproving(true);
    await approveSuggestion();
    setIsApproving(false);
  };

  const canMutate = currentSuggestion.type === 'move_stage';

  return (
    <div className="flex flex-col gap-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/80 text-foreground shadow-xs">
            <Activity className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden />
          </span>
          <span className="text-xs font-medium text-muted-foreground">Insights</span>
          {pendingSuggestions.length > 1 && (
            <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
              {pendingSuggestions.length} pending
            </span>
          )}
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={closeWidget}
          aria-label="Close Flow"
        >
          <X className="size-3.5" />
        </Button>
      </div>

      {/* Suggestion card */}
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSuggestion.contactId + currentSuggestion.type}
          initial={{ opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -12 }}
          transition={{ type: 'spring', stiffness: 400, damping: 28 }}
          className="mx-3 mb-3 rounded-md border border-border bg-muted/40 p-3"
        >
          {/* Urgency + type indicator */}
          <div className="mb-2.5 flex items-center gap-2">
            <span
              className="flex h-5 w-5 items-center justify-center rounded-md"
              style={{
                color: urgencyColor[currentSuggestion.urgency],
                background: `${urgencyColor[currentSuggestion.urgency]}18`,
              }}
            >
              <SuggestionIcon type={currentSuggestion.type} />
            </span>
            <span
              className="text-[10px] font-semibold uppercase tracking-wider"
              style={{ color: urgencyColor[currentSuggestion.urgency] }}
            >
              {currentSuggestion.urgency} priority
            </span>
          </div>

          {/* Contact info */}
          {contact && (
            <div className="mb-2.5 flex items-center gap-2.5">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-secondary text-xs font-semibold text-secondary-foreground">
                {contact.name
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .slice(0, 2)}
              </span>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">
                  {contact.name}
                </p>
                {contact.company && (
                  <p className="truncate text-xs text-muted-foreground">{contact.company}</p>
                )}
              </div>
            </div>
          )}

          {/* Reason */}
          <p className="mb-3 text-xs leading-relaxed text-muted-foreground">
            {currentSuggestion.reason}
          </p>

          {/* Stage target (for move_stage) */}
          {currentSuggestion.type === 'move_stage' && currentSuggestion.targetStage && (
            <div className="mb-3 flex items-center gap-1.5 rounded-md border border-primary/25 bg-primary/10 px-2.5 py-1.5">
              <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                Move to
              </span>
              <ChevronRight className="h-3 w-3 text-muted-foreground" aria-hidden />
              <span className="text-xs font-semibold capitalize text-primary">
                {currentSuggestion.targetStage.replace('_', ' ')}
              </span>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-2">
            {canMutate ? (
              <Button
                type="button"
                size="sm"
                className="h-8 flex-1 gap-1.5 text-xs"
                onClick={handleApprove}
                disabled={isApproving}
              >
                {isApproving ? (
                  <Loader2 className="size-3.5 animate-spin" aria-hidden />
                ) : (
                  <Check className="size-3.5" />
                )}
                {isApproving ? 'Moving…' : 'Approve'}
              </Button>
            ) : (
              contact && (
                <Button
                  type="button"
                  size="sm"
                  className="h-8 flex-1 gap-1.5 text-xs"
                  onClick={() => requestDraft(contact)}
                >
                  <Mail className="size-3.5" />
                  Draft email
                </Button>
              )
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="h-8 px-3 text-xs"
              onClick={dismissSuggestion}
            >
              Dismiss
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>

      {/* Queue indicator */}
      {pendingSuggestions.length > 1 && (
        <div className="flex justify-center gap-1 pb-3">
          {pendingSuggestions.map((_, i) => (
            <span
              key={i}
              className={cn(
                'h-1 rounded-full transition-all duration-300',
                i === 0
                  ? 'w-4 bg-primary'
                  : 'w-1 bg-muted-foreground/25'
              )}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function DraftingPanel() {
  const { currentDraft, approveDraft, dismissDraft, closeWidget } = useFlow();
  const [copied, setCopied] = useState(false);

  if (!currentDraft) return null;

  const handleCopy = () => {
    approveDraft();
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex flex-col gap-0">
      {/* Header */}
      <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/80 text-foreground shadow-xs">
            <Mail className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden />
          </span>
          <span className="text-xs font-medium text-muted-foreground">Email draft</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={closeWidget}
          aria-label="Close Flow"
        >
          <X className="size-3.5" />
        </Button>
      </div>

      <div className="mx-3 mb-3 space-y-2.5">
        {/* Contact */}
        <div className="flex items-center gap-2 rounded-md border border-border bg-muted/40 px-3 py-2">
          <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">To</span>
          <span className="text-xs font-medium text-foreground">{currentDraft.contactName}</span>
          <span className="text-xs text-muted-foreground">·</span>
          <span className="truncate text-xs text-muted-foreground">{currentDraft.contactEmail}</span>
        </div>

        {/* Subject */}
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5">
          <p className="mb-0.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Subject
          </p>
          <p className="text-sm font-medium text-foreground">{currentDraft.subject}</p>
        </div>

        {/* Preview */}
        <div className="rounded-md border border-border bg-muted/40 px-3 py-2.5">
          <p className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
            Preview
          </p>
          <p className="text-xs leading-relaxed text-muted-foreground">{currentDraft.preview}</p>
        </div>

        {/* CTA chip */}
        <div className="flex items-center gap-1.5 rounded-md border border-primary/20 bg-primary/5 px-2.5 py-1.5">
          <ChevronRight className="h-3 w-3 text-primary" aria-hidden />
          <span className="text-xs font-medium text-foreground">
            {currentDraft.ctaSuggestion}
          </span>
        </div>

        {/* Actions */}
        <div className="flex gap-2 pt-0.5">
          <Button type="button" size="sm" className="h-8 flex-1 gap-1.5 text-xs" onClick={handleCopy}>
            {copied ? (
              <>
                <Check className="size-3.5" />
                Copied
              </>
            ) : (
              <>
                <Copy className="size-3.5" />
                Copy draft
              </>
            )}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-8 px-3 text-xs" onClick={dismissDraft}>
            Back
          </Button>
        </div>
      </div>
    </div>
  );
}

// ─── Idle / Empty State Panel ────────────────────────────────────────────────

function IdlePanel() {
  const { closeWidget } = useFlow();
  return (
    <div className="flex flex-col gap-0">
      <div className="flex items-center justify-between px-4 pt-3 pb-2.5">
        <div className="flex items-center gap-2">
          <span className="flex h-7 w-7 items-center justify-center rounded-md border border-border bg-muted/80 text-foreground shadow-xs">
            <Activity className="h-3.5 w-3.5 shrink-0" strokeWidth={1.5} aria-hidden />
          </span>
          <span className="text-xs font-medium text-muted-foreground">Status</span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="text-muted-foreground"
          onClick={closeWidget}
          aria-label="Close Flow"
        >
          <X className="size-3.5" />
        </Button>
      </div>
      <div className="mx-3 mb-4 rounded-md border border-border bg-muted/40 px-4 py-4 text-left">
        <div className="mb-2 flex items-center gap-2">
          <CheckCircle2 className="size-4 shrink-0 text-primary" aria-hidden />
          <p className="text-sm font-medium text-foreground">No actions pending</p>
        </div>
        <p className="text-xs leading-relaxed text-muted-foreground">
          Flow monitors your pipeline and surfaces actions when contacts need attention.
        </p>
      </div>
    </div>
  );
}

async function callFlowChat(message: string): Promise<string> {
  const { data: { session } } = await supabase.auth.getSession();
  const token = session?.access_token;
  if (!token) {
    return 'Your session expired. Please sign in again.';
  }
  try {
    const res = await fetch('/api/chat/ai', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ message }),
    });

    if (res.status === 403) {
      return "I'm here to help with startup fundraising and investor discovery on RaiseBeacon. What can I help you with today?";
    }
    if (res.status === 401) {
      return 'Your session expired. Please sign in again.';
    }
    if (!res.ok) {
      throw new Error('Flow chat request failed');
    }

    const data = await res.json();
    return (data.reply as string) ?? 'No response returned.';
  } catch {
    return 'I could not reach Flow right now. Please try again in a moment.';
  }
}

function CompactChatPanel() {
  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <AiAssistat
        embedded
        emptyHeading="How can I help you today?"
        description="Interact with an intelligent assistant that understands your queries and provides instant responses."
        onSend={callFlowChat}
        className="min-h-0 flex-1"
        emptyStateIcon={
          <span className="mb-3 flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-full border border-indigo-400/40 bg-slate-900/50 shadow-inner sm:mb-4">
            <SiriOrb
              size="52px"
              animationDuration={20}
              className="pointer-events-none"
            />
          </span>
        }
      />
    </div>
  );
}

// ─── Inline launcher (e.g. Tasks header next to Calendar) ─────────────────────

export function FlowInlineLauncher({ className }: { className?: string }) {
  const {
    openWidgetWithInlineAnchor,
    closeWidget,
    isOpen,
    suggestionsCount,
    inlineFlowAnchorRef,
  } = useFlow();

  useEffect(() => {
    return () => {
      inlineFlowAnchorRef.current = null;
    };
  }, [inlineFlowAnchorRef]);

  return (
    <motion.button
      type="button"
      ref={(node) => {
        inlineFlowAnchorRef.current = node;
      }}
      onClick={() => (isOpen ? closeWidget() : openWidgetWithInlineAnchor())}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 400, damping: 24 }}
      aria-label={isOpen ? 'Close Flow by RaiseBeacon' : 'Open Flow by RaiseBeacon'}
      aria-expanded={isOpen}
      className={cn(
        'relative flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full border border-border/70 bg-card/90 p-1 shadow-sm ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:border-primary/40 dark:bg-card/80 dark:ring-white/10',
        focusRing,
        className
      )}
    >
      <SiriOrb
        size="36px"
        animationDuration={16}
        className="pointer-events-none drop-shadow-sm"
      />
      <AnimatePresence>
        {suggestionsCount > 0 && (
          <motion.span
            key="badge-inline"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 500, damping: 20 }}
            className="absolute -right-0.5 -top-0.5 z-10 flex h-4 min-w-4 items-center justify-center rounded-full border border-background bg-destructive px-0.5 text-[9px] font-medium text-destructive-foreground shadow-sm"
            aria-label={`${suggestionsCount} pending suggestions`}
          >
            {suggestionsCount}
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

// ─── Main Widget ──────────────────────────────────────────────────────────────

export function FlowWidget({ panelOnly = false }: { panelOnly?: boolean }) {
  const {
    widgetState,
    isOpen,
    suggestionsCount,
    openWidget,
    closeWidget,
    inlineFlowAnchorRef,
    panelAnchorSnapshot,
  } = useFlow();
  const panelRef = useRef<HTMLDivElement>(null);
  const [activePanel, setActivePanel] = useState<'insights' | 'chat'>('insights');
  /** Bumped on resize/scroll while open so anchored panel follows the orb (not on tab/content changes). */
  const [anchorLayoutVersion, setAnchorLayoutVersion] = useState(0);

  const showPanel = isOpen;

  useEffect(() => {
    if (!isOpen) {
      setActivePanel('insights');
      setAnchorLayoutVersion(0);
    }
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!panelOnly || !isOpen) return;

    const bump = () => setAnchorLayoutVersion((v) => v + 1);
    window.addEventListener('resize', bump);
    window.addEventListener('scroll', bump, true);
    return () => {
      window.removeEventListener('resize', bump);
      window.removeEventListener('scroll', bump, true);
    };
  }, [panelOnly, isOpen]);

  const anchorStyle = useMemo(() => {
    if (!panelOnly || !isOpen) return undefined;
    if (typeof window === 'undefined') return undefined;
    if (anchorLayoutVersion === 0 && panelAnchorSnapshot) {
      return panelAnchorSnapshot;
    }
    return computePanelAnchorStyle(
      inlineFlowAnchorRef.current,
      window.innerWidth,
      window.innerHeight
    );
  }, [panelOnly, isOpen, anchorLayoutVersion, inlineFlowAnchorRef, panelAnchorSnapshot]);

  const panelCard = (
    <motion.div
      ref={panelRef}
      key="flow-panel"
      initial={panelOnly ? { opacity: 0, y: 0, scale: 1 } : false}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={
        panelOnly
          ? { opacity: 0, transition: { duration: 0.14, ease: 'easeIn' } }
          : false
      }
      transition={
        panelOnly
          ? { opacity: { duration: 0.18, ease: [0.25, 0.1, 0.25, 1] } }
          : { duration: 0 }
      }
      className={cn(
        'flex flex-col overflow-hidden rounded-md border border-slate-800/80 bg-slate-950/95 text-slate-100 shadow-xl backdrop-blur-md',
        !panelOnly &&
          'h-[min(560px,calc(100vh-5.5rem))] max-h-[min(560px,calc(100vh-5.5rem))] w-[min(392px,calc(100vw-1.5rem))]',
        panelOnly && 'min-h-0'
      )}
      style={panelOnly ? anchorStyle : undefined}
      {...(panelOnly
        ? { role: 'complementary' as const, 'aria-label': 'Flow by RaiseBeacon' }
        : {})}
    >
      <div className="shrink-0 border-b border-border px-4 py-3">
        <div className="mb-3 flex items-center gap-2">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center overflow-hidden rounded-full border border-border bg-muted/50 shadow-xs">
                  <SiriOrb size="36px" animationDuration={22} className="scale-100" />
                </span>
                <div className="min-w-0">
                  <p className="text-sm font-semibold leading-tight text-foreground">Flow by RaiseBeacon</p>
                  <p className="truncate text-xs text-muted-foreground">
                    Pipeline insights and assistant chat
                  </p>
                </div>
              </div>
              <div
                className="flex rounded-md border border-border bg-muted/50 p-1"
                role="tablist"
                aria-label="Flow panel"
              >
                <button
                  type="button"
                  role="tab"
                  aria-selected={activePanel === 'insights'}
                  onClick={() => setActivePanel('insights')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-medium transition-colors',
                    focusRing,
                    activePanel === 'insights'
                      ? 'bg-background text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <Activity className="size-3 shrink-0" strokeWidth={1.5} aria-hidden />
                  Insights
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={activePanel === 'chat'}
                  onClick={() => setActivePanel('chat')}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded-sm px-2 py-1.5 text-xs font-medium transition-colors',
                    focusRing,
                    activePanel === 'chat'
                      ? 'bg-background text-foreground shadow-xs'
                      : 'text-muted-foreground hover:text-foreground'
                  )}
                >
                  <MessageSquare className="size-3 shrink-0" strokeWidth={1.5} aria-hidden />
                  Chat
                </button>
              </div>
            </div>

            <div className="flex min-h-0 flex-1 flex-col overflow-hidden overscroll-contain">
              <AnimatePresence mode="wait">
                {activePanel === 'chat' && (
                  <motion.div
                    key="chat"
                    initial={false}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.12 }}
                    className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-b-md bg-gradient-to-b from-indigo-950/35 via-slate-950/50 to-indigo-950/60"
                  >
                    <CompactChatPanel />
                  </motion.div>
                )}
                {activePanel === 'insights' && widgetState === 'idle' && (
                  <motion.div
                    key="idle"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
                  >
                    <IdlePanel />
                  </motion.div>
                )}
                {activePanel === 'insights' && widgetState === 'suggestion' && (
                  <motion.div
                    key="suggestion"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
                  >
                    <SuggestionPanel />
                  </motion.div>
                )}
                {activePanel === 'insights' && widgetState === 'drafting' && (
                  <motion.div
                    key="drafting"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.15 }}
                    className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain"
                  >
                    <DraftingPanel />
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
    </motion.div>
  );

  if (panelOnly) {
    return (
      <AnimatePresence initial={false}>
        {showPanel && anchorStyle ? panelCard : null}
      </AnimatePresence>
    );
  }

  return (
    <div
      className="pointer-events-none fixed bottom-24 right-6 z-50"
      role="complementary"
      aria-label="Flow by RaiseBeacon"
    >
      {/*
        Launcher lives in a fixed-size box so swapping orb ↔ close never changes layout height.
        Previously the panel sat in a flex column above an exiting IdleOrb; AnimatePresence kept
        the orb in-flow during exit, the column grew, then shrunk — panel jumped downward.
      */}
      <div className="pointer-events-auto relative h-[5.5rem] w-[5.5rem] shrink-0">
        <AnimatePresence initial={false}>
          {showPanel ? (
            <motion.button
              key="flow-close"
              type="button"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.14, ease: [0.25, 0.1, 0.25, 1] }}
              whileHover={{ scale: 1.04 }}
              whileTap={{ scale: 0.97 }}
              onClick={closeWidget}
              aria-label="Close Flow panel"
              className={cn(
                'group absolute inset-0 flex cursor-pointer items-center justify-center rounded-full border border-border/70 bg-card/90 p-1 shadow-md ring-1 ring-black/5 backdrop-blur-sm transition-colors hover:border-primary/35 dark:ring-white/10',
                focusRing
              )}
            >
              <SiriOrb size="52px" animationDuration={18} className="pointer-events-none opacity-90" />
              <span className="pointer-events-none absolute inset-0 flex items-center justify-center rounded-full bg-background/60 opacity-0 transition-opacity group-hover:opacity-100 dark:bg-background/70">
                <X className="size-5 text-foreground" strokeWidth={2} aria-hidden />
              </span>
            </motion.button>
          ) : (
            <motion.div
              key="flow-launcher"
              className="absolute inset-0 flex items-center justify-center"
              initial={false}
            >
              <IdleOrb count={suggestionsCount} onClick={openWidget} />
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence initial={false}>
          {showPanel ? (
            <motion.div
              key="flow-panel-shell"
              className="pointer-events-auto absolute bottom-full right-0 z-10 mb-2 w-[min(392px,calc(100vw-1.5rem))] max-w-[calc(100vw-1.5rem)]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, transition: { duration: 0.14, ease: 'easeIn' } }}
              transition={{ duration: 0.18, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {panelCard}
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}

export default FlowWidget;
